import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, Inject, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Business, Country } from '../../../models';
import {
  BusinessService,
  CountryService,
  ThemeService,
} from '../../../services';
import { LogoService, ToastAlertService } from '@core/services';
import { Theme } from '../../../models';
import { Cookies } from 'typescript-cookie';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { ButtonComponent } from '@shared/components/button/button.component';
import { JsonPipe, NgIf } from '@angular/common';
import { TextAreaComponent } from '@shared/components/text-area/text-area.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { TitleComponent } from '@shared/components/title/title.component';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'app-company-modal',
  templateUrl: './company-modal.component.html',
  styleUrls: [],
  standalone: true,
  imports: [
    MatCardModule,
    TitleComponent,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    ReactiveFormsModule,
    MatDialogContent,
    InputComponent,
    SelectComponent,
    TextAreaComponent,
    NgIf,
    MatDialogActions,
    ButtonComponent,
    MatDialogClose,
    JsonPipe,
  ],
})
export class CompanyModalComponent {
  private formBuilder = inject(FormBuilder);
  public operation = '';
  companyForm!: FormGroup;
  buildingForm!: FormGroup;
  public workplaceTypeForm!: FormGroup;
  public floorForm!: FormGroup;
  countrys: Country[] = [];
  public logoBase64: any;

  //SERVICES
  private alertService = inject(ToastAlertService);
  private countryService = inject(CountryService);
  private logoService = inject(LogoService);
  private businessService = inject(BusinessService);
  private themeService = inject(ThemeService);
  private destroyRef = inject(DestroyRef);
  private dialogRef = inject(MatDialogRef<CompanyModalComponent>);

  //VARS
  public countries = signal<Country[]>([]);
  public themes = signal<Theme[]>([]);
  public reader = signal<void | null>(null);
  public logo = signal<string>('');
  idLogoGlobal?: number;

  imageError: string | null = null;
  isImageSaved?: boolean;
  imgBase64 = signal<string>('');

  constructor(@Inject(MAT_DIALOG_DATA) public data: Business) {
    this.getCountries();
    this.getThemes();
    this.buildForm();
    if (this.data) {
      this.loadLogo();
    }
  }

  selectedFile: any = null;

  onFileSelected(event: Event) {
    const inputElement = event.target as HTMLInputElement;

    if (inputElement.files && inputElement.files[0]) {
      const max_size = 5242880;
      const allowed_types = ['image/png', 'image/jpeg'];
      //const max_height = 5000;
      //const max_width = 4000;

      if (inputElement.files[0].size > max_size) {
        this.imageError =
          'El tamaño máximo es ' + Math.trunc(max_size) / 1000000 + 'Mb';
        return false;
      }

      if (!allowed_types.includes(inputElement.files[0].type)) {
        this.imageError = 'Solo documentos ( JPG | PNG )';
        return false;
      }

      const reader = new FileReader();
      reader.onload = (e: ProgressEvent) => {
        const image = new Image();
        image.src = (e.target as any).result;

        image.onload = rs => {
          const img_height = (rs.currentTarget as HTMLImageElement).height;
          const img_width = (rs.currentTarget as HTMLImageElement).width;

          console.log(img_height, img_width);

          const imgBase64Path = (e.target as any).result;
          this.imgBase64.update(() => imgBase64Path);
          this.isImageSaved = true;
        };
      };

      reader.readAsDataURL(inputElement.files[0]);
    }

    return true;
  }

  public onSubmit() {
    if (this.companyForm.valid) {
      if (this.data) {
        this.businessService
          .putBusiness(
            this.companyForm.value.idBusiness,
            this.companyForm.value,
            this.imgBase64()
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: ({ code, detail }) => {
              if (code === 10000) {
                this.dialogRef.close(true);
                this.alertService.sucess('Empresa modificada exitosamente');
              } else {
                this.alertService.error(detail);
              }
            },
            error: error => {
              this.alertService.error(error);
            },
          });
      } else {
        if (this.imgBase64()) {
          this.businessService
            .postBusiness(this.companyForm.value, this.imgBase64())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: ({ code, detail }) => {
                if (code === 10000) {
                  this.dialogRef.close(true);
                  this.alertService.sucess('Empresa agregada exitosamente');
                } else {
                  this.alertService.error(detail);
                }
              },
              error: error => {
                this.alertService.error(error);
              },
            });
        } else {
          this.alertService.error('El logo es obligatorio');
        }
      }
    }
  }

  clearForm() {
    this.buildForm();
  }

  private loadLogo() {
    this.logoService
      .getOneLogo(this.data.idLogo as number)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ reply }) => {
          this.imgBase64.set(reply.value), (this.isImageSaved = true);
        },
      });
  }

  private getCountries(): void {
    this.countryService
      .getCountrys()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          if (resp.code === 10000) {
            this.countries.set(resp.reply);
          } else {
            this.alertService.error(resp.detail);
          }
        },
        error: error => {
          console.log(error);
          this.alertService.error(`Error: ${error}`);
        },
      });
  }

  private getThemes(): void {
    this.themeService
      .getThemes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ reply, code, detail }) => {
          if (code === 10000) this.themes.set(reply);
          else {
            this.alertService.error(detail);
          }
        },
        error: error => {
          console.log(error);
          this.alertService.error(`Error: ${error}`);
        },
      });
  }

  private buildForm(): void {
    if (this.data) {
      this.companyForm = this.formBuilder.group({
        idBusiness: [this.data.idBusiness],
        name: [this.data.name, Validators.required],
        nit: [
          this.data.nit,
          [
            Validators.required,
            Validators.minLength(7),
            Validators.maxLength(20),
          ],
        ],
        address: [
          this.data.address,
          [Validators.required, Validators.maxLength(256)],
        ],
        code: [this.data.code, [Validators.required, Validators.maxLength(20)]],
        idCountry: [this.data.idCountry?.toString() || '', Validators.required],
        manager: [this.data.manager, Validators.required],
        managerContact: [
          this.data.managerContact,
          [Validators.required, Validators.email],
        ],
        contractNumber: [
          this.data.contractNumber,
          [
            Validators.required,
            Validators.minLength(5),
            Validators.pattern('^\\d+(-\\d+)*$'),
          ],
        ],
        invoiceNumber: [
          this.data.invoiceNumber,
          [
            Validators.required,
            Validators.minLength(5),
            Validators.pattern('^\\d+(-\\d+)*$'),
          ],
        ],
        idTheme: [this.data.idTheme, Validators.required],
        createdBy: [Cookies.get('workerId') as string],
      });
    } else {
      this.companyForm = this.formBuilder.group({
        name: ['', Validators.required],
        nit: [
          '',
          [
            Validators.required,
            Validators.minLength(7),
            Validators.maxLength(20),
          ],
        ],
        address: ['', [Validators.required, Validators.maxLength(256)]],
        code: ['', [Validators.required, Validators.maxLength(20)]],
        idCountry: ['', Validators.required],
        manager: ['', Validators.required],
        managerContact: ['', [Validators.required, Validators.email]],
        contractNumber: [
          '',
          [
            Validators.required,
            Validators.minLength(5),
            Validators.pattern('^\\d+(-\\d+)*$'),
          ],
        ],
        invoiceNumber: [
          '',
          [
            Validators.required,
            Validators.minLength(5),
            Validators.pattern('^\\d+(-\\d+)*$'),
          ],
        ],
        usersCount: [
          '',
          [
            Validators.required,
            Validators.min(1),
            Validators.pattern('[0-9]*'),
          ],
        ],
        idTheme: ['', Validators.required],
        createdBy: [Cookies.get('workerId') as string],
      });
    }
  }

  preventDecimal(event: KeyboardEvent) {
    if (
      event.key === '.' ||
      event.key === ',' ||
      event.key === 'e' ||
      event.key === '-' ||
      event.key === '+'
    ) {
      event.preventDefault();
    }
  }
}
